import { useQuery } from "@tanstack/react-query";
import type { PaginationState } from "@tanstack/react-table";
import { Building05 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Switch } from "react-aria-components";
import {
	type ColumnDef,
	FeaturedIconCell,
	PrimaryTextOrEmptyCell,
} from "@/components/Molecules/Table";
import { TableListEmptyState } from "@/components/Molecules/Table/TableListEmptyState";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { TableView } from "@/components/Molecules/Table/TableView";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	organizationQueryOptions,
	SUB_ORGANIZATION_MAX_PAGE_SIZE,
} from "@/services/organization";
import { showSuccessToast } from "@/utils/toast.utils";

export interface EntityTypeAccessSectionProps {
	orgId: string;
	/** `enabled === false` is off. Missing means the organization grant is on. */
	typeEnabled: boolean;
	grantedIds: string[];
	setDraftSubOrgIds: (next: string[] | null) => void;
	canEdit: boolean;
	entityLabel: string;
	enabledLabel: string;
	disabledLabel: string;
	switchTestId: string;
	tableKey: string;
	emptyTitle: string;
	grantSubOrg: (subOrgId: string) => Promise<unknown>;
	revokeSubOrg: (subOrgId: string) => Promise<unknown>;
	enableOrganization: () => Promise<unknown>;
	disableOrganization: () => Promise<unknown>;
}

interface AccessSubOrgRow {
	id: string;
	name: string | null;
}

function sameIds(left: Set<string>, right: string[]): boolean {
	if (left.size !== right.length) return false;
	return right.every((id) => left.has(id));
}

/**
 * Header select-all reports only the current page. Keep grants that are not on that page.
 */
function mergePageSelection(
	previous: Set<string>,
	next: Set<string>,
	pageIds: string[],
): Set<string> {
	const merged = new Set(previous);
	for (const id of pageIds) {
		if (next.has(id)) merged.add(id);
		else merged.delete(id);
	}
	return merged;
}

export function EntityTypeAccessSection({
	orgId,
	typeEnabled,
	grantedIds,
	setDraftSubOrgIds,
	canEdit,
	entityLabel,
	enabledLabel,
	disabledLabel,
	switchTestId,
	tableKey,
	emptyTitle,
	grantSubOrg,
	revokeSubOrg,
	enableOrganization,
	disableOrganization,
}: EntityTypeAccessSectionProps) {
	const { t } = useAppTranslation();
	const [filter, setFilter] = useState("");
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [pendingIds, setPendingIds] = useState<Set<string> | null>(null);
	const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const subOrgCountQuery = useQuery(
		organizationQueryOptions.subOrganizations.count(orgId, {}),
	);
	const subOrgListQuery = useQuery(
		organizationQueryOptions.subOrganizations.list(
			orgId,
			1,
			SUB_ORGANIZATION_MAX_PAGE_SIZE,
			{},
		),
	);

	const total = subOrgCountQuery.data ?? 0;
	const hasSubOrganizations = total > 0;
	const subOrgs = useMemo<AccessSubOrgRow[]>(() => {
		return (subOrgListQuery.data ?? [])
			.filter((row): row is typeof row & { id: string } => row.id !== null)
			.map((row) => ({ id: row.id, name: row.name ?? null }));
	}, [subOrgListQuery.data]);

	const grantedKey = grantedIds.slice().sort().join("\0");
	const granted = useMemo(
		() => new Set(grantedKey.length === 0 ? [] : grantedKey.split("\0")),
		[grantedKey],
	);
	const selectedIds = pendingIds ?? granted;
	const orgEnabled = pendingEnabled ?? typeEnabled;

	useEffect(() => {
		setPendingIds((current) =>
			current && sameIds(current, grantedIds) ? null : current,
		);
	}, [grantedKey, grantedIds]);

	useEffect(() => {
		if (pendingEnabled !== null && pendingEnabled === typeEnabled) {
			setPendingEnabled(null);
		}
	}, [pendingEnabled, typeEnabled]);
	const subOrganizationLabel = t("subOrganization.entityLabel");

	const columns = useMemo<ColumnDef<AccessSubOrgRow>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				header: t("admin.entityTypes.access.nameColumn"),
				enableSorting: true,
				meta: { headerProps: { isRowHeader: true } },
				cell: () => (
					<span className="flex min-w-0 items-center gap-2">
						<FeaturedIconCell icon={Building05} />
						<PrimaryTextOrEmptyCell />
					</span>
				),
			},
		],
		[t],
	);

	const query = filter.trim().toLowerCase();
	const visibleRows = useMemo(() => {
		const source = canEdit
			? subOrgs
			: subOrgs.filter((row) => granted.has(row.id));
		if (!query) return source;
		return source.filter((row) =>
			(row.name ?? "").toLowerCase().includes(query),
		);
	}, [canEdit, granted, query, subOrgs]);

	const nameOf = (id: string) =>
		subOrgs.find((row) => row.id === id)?.name || id;

	const applySelection = async (next: Set<string>) => {
		if (!canEdit || isSaving || sameIds(next, Array.from(selectedIds))) return;
		const current = selectedIds;
		setPendingIds(next);
		setDraftSubOrgIds(Array.from(next));
		setIsSaving(true);
		const grantedNow: string[] = [];
		const removedNow: string[] = [];
		for (const id of next) {
			if (!current.has(id)) grantedNow.push(id);
		}
		for (const id of current) {
			if (!next.has(id)) removedNow.push(id);
		}
		try {
			await Promise.all([
				...grantedNow.map(async (id) => {
					await grantSubOrg(id);
					showSuccessToast({
						message: t("admin.entityTypes.access.toast.granted", {
							name: nameOf(id),
						}),
					});
				}),
				...removedNow.map(async (id) => {
					await revokeSubOrg(id);
					showSuccessToast({
						message: t("admin.entityTypes.access.toast.removed", {
							name: nameOf(id),
						}),
					});
				}),
			]);
		} catch {
			setPendingIds(null);
			setDraftSubOrgIds(Array.from(granted));
		} finally {
			setIsSaving(false);
		}
	};

	const applyOrganization = async (enabled: boolean) => {
		if (!canEdit || isSaving || enabled === orgEnabled) return;
		setPendingEnabled(enabled);
		setDraftSubOrgIds(enabled ? null : []);
		setIsSaving(true);
		try {
			if (enabled) {
				await enableOrganization();
				showSuccessToast({
					message: t("admin.entityTypes.access.toast.enabled"),
				});
			} else {
				await disableOrganization();
				showSuccessToast({
					message: t("admin.entityTypes.access.toast.disabled"),
				});
			}
		} catch {
			setPendingEnabled(null);
			setDraftSubOrgIds(typeEnabled ? null : []);
		} finally {
			setIsSaving(false);
		}
	};

	if (subOrgCountQuery.isPending || subOrgListQuery.isPending) {
		return (
			<p className="text-sm text-tertiary">
				{t("common.actions.loadingEllipsis")}
			</p>
		);
	}

	if (!hasSubOrganizations) {
		return (
			<div className="flex items-center justify-between gap-4 rounded-xl border border-secondary bg-primary px-4 py-4">
				<div className="min-w-0">
					<p className="text-sm font-semibold text-primary">
						{t("admin.entityTypes.access.orgTitle", { entity: entityLabel })}
					</p>
					<p className="mt-0.5 text-sm text-tertiary">
						{t("admin.entityTypes.access.orgDescription", {
							entity: entityLabel,
						})}
					</p>
				</div>
				<div className="flex shrink-0 items-center gap-3">
					<span className="text-sm font-medium text-secondary">
						{orgEnabled ? enabledLabel : disabledLabel}
					</span>
					{canEdit ? (
						<Switch
							isSelected={orgEnabled}
							onChange={(checked) => void applyOrganization(checked)}
							isDisabled={isSaving}
							data-testid={switchTestId}
							aria-label={t("admin.entityTypes.access.orgTitle", {
								entity: entityLabel,
							})}
							className="group inline-flex shrink-0 cursor-pointer items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
						>
							<span className="relative inline-flex h-5 w-9 items-center rounded-full bg-bg-quaternary transition-colors group-selected:bg-brand-500 group-data-disabled:cursor-not-allowed group-data-disabled:opacity-50">
								<span className="inline-block size-4 translate-x-0.5 transform rounded-full bg-white shadow transition-transform group-selected:translate-x-4" />
							</span>
						</Switch>
					) : null}
				</div>
			</div>
		);
	}

	const pageStart = pagination.pageIndex * pagination.pageSize;
	const pageIds = visibleRows
		.slice(pageStart, pageStart + pagination.pageSize)
		.map((row) => row.id);

	return (
		<div className="flex w-full flex-col gap-1">
			<p className="text-sm text-secondary">
				{t("admin.entityTypes.access.prompt", {
					subOrganization: subOrganizationLabel,
					entity: entityLabel,
				})}
			</p>
			<TableView
				tableKey={tableKey}
				data={visibleRows}
				columns={columns}
				size="sm"
				showHeaderPadding={false}
				enableSorting
				enableTableActions
				filterLayout="bar"
				filterValue={filter}
				onFilterChange={(value) => {
					setFilter(value);
					setPagination((current) => ({ ...current, pageIndex: 0 }));
				}}
				filterPlaceholder={t("admin.entityTypes.access.search")}
				enableRowSelection={canEdit}
				selectedKeys={selectedIds}
				selectedCount={canEdit ? selectedIds.size : undefined}
				getRowId={(row) => row.id}
				onSelectionChange={(keys) => {
					void applySelection(
						mergePageSelection(selectedIds, keys, pageIds),
					);
				}}
				paginationMode="client"
				externalPaginationState={pagination}
				onPaginationChange={setPagination}
				rowCount={visibleRows.length}
				isLoading={subOrgListQuery.isFetching && subOrgs.length === 0}
				emptyState={
					query ? (
						<TableSearchEmptyState />
					) : (
						<TableListEmptyState title={emptyTitle} />
					)
				}
			/>
		</div>
	);
}
